<template>
  <div class="c-editor-hyperlink max-w-[530px]" @click.stop="handleClick">
    <NInput
      ref="inputRef"
      v-model:value="link"
      autofocus
      :placeholder="t('canvasUI.CHyperLink.addYourLinkHere')"
      @keydown.esc="exit"
      @keyup.enter="insertLink"
    >
      <template #prefix>
        <CIcon icon="link" size="16px" />
      </template>
      <template #suffix>
        <div class="flex items-center">
          <CButton v-if="isLinkChanged" circle class="ml-2" size="small" type="primary" @click.stop="insertLink">
            <template #icon>
              <CIcon icon="save" size="14px" />
            </template>
          </CButton>
          <CButton v-if="link" circle class="ml-2" size="small" @click.stop="openLink">
            <template #icon>
              <CIcon icon="external-link-alt" size="14px" />
            </template>
          </CButton>
          <CButton v-if="link" circle class="ml-2" size="small" @click.stop="removeLink">
            <template #icon>
              <CIcon icon="circle-xmark" size="14px" />
            </template>
          </CButton>
        </div>
      </template>
    </NInput>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, computed, inject } from 'vue'
import { NInput } from 'naive-ui'
import { useI18n } from 'vue-i18n'
import type { Editor } from '@tiptap/vue-3'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { prefixHttps } from '@lib/util/string'
import { type CanvasBuilderProps } from '@canvas-builder/types/canvas'

const openExternalUrl = inject<CanvasBuilderProps['onOpenExternalUrl']>('openExternalUrl')

interface Props {
  editor: Editor
}

const props = defineProps<Props>()
const emit = defineEmits<{
  close: []
}>()
const { t } = useI18n()
const themeVars = useThemeVars()
const originalLink = ref(props.editor.getAttributes('link').href || '')
const link = ref(props.editor.getAttributes('link').href || '')
const inputRef = ref()

const isLinkChanged = computed(() => link.value !== originalLink.value)

watch(
  () => props.editor.getAttributes('link').href as string,
  (newLink: string) => {
    if (newLink && newLink !== originalLink.value) {
      originalLink.value = newLink
      link.value = newLink
    }
  },
)

function exit() {
  emit('close')
}

function removeLink() {
  props.editor.chain().focus().extendMarkRange('link').unsetLink().run()
  exit()
}

function insertLink() {
  if (!link.value?.length) return removeLink()

  const fixedLink = prefixHttps(link.value)
  const isInsideLink = props.editor.isActive('link')

  if (props.editor.state.selection.empty && !isInsideLink) {
    // Insert the URL as text if no text is selected, note the extra space after the link
    props.editor.chain().focus().insertContent(`<a href="${fixedLink}">${link.value}</a> `).run()
  } else {
    // Otherwise, set the selected text as a link
    props.editor.chain().focus().extendMarkRange('link').setLink({ href: fixedLink }).run()
  }

  originalLink.value = link.value
  exit()
}

function handleClick() {
  inputRef.value?.focus()
}

function openLink() {
  if (link.value) {
    openExternalUrl?.({ url: prefixHttps(link.value), target: '_blank' })
  }
}
</script>

<style lang="scss">
.hyper-link-container {
  background-color: v-bind('themeVars.primary6');
  border: 1px solid v-bind('themeVars.primary5');
}
</style>
